// Chi lavora in una sessione e cosa sta facendo, detto per una persona (2026-11-02: «sarebbe da
// mettere cosa sta facendo, non il comando»). Lo usano «Chi sta facendo cosa» nella Dashboard e la
// crew nella chat. Le righe tecniche della traccia (il CLI lanciato, i commit, i token) restano
// nel Runtime.
import { SESSION_META } from '../components/statusMeta';
import type { AgentAtWork } from '../store/selectors';
import type { MissionTask, Session, TraceEntry } from '../types';

/** Un pop è «al lavoro» se ha un lavoro in corso o ha scritto nella traccia da poco. */
export const ACTIVE_MS = 90_000;

const SAID = new Set(['thought', 'decision', 'handoff']);

/** La prima riga di un commento, senza i segni del Markdown. */
export function plain(text: string): string {
  return text
    .trim()
    .split('\n')[0]
    .replace(/[*_`#>]/g, '');
}

/** L'ultima cosa detta da `agent` nella traccia, in una riga; null se ha solo comandi o note. */
export function lastSaid(trace: TraceEntry[], agent: string): string | null {
  let best: TraceEntry | null = null;
  for (const t of trace)
    if (t.instanceId === agent && SAID.has(t.kind) && !t.streaming && t.text.trim())
      if (!best || t.seq > best.seq) best = t;
  return best ? plain(best.text) : null;
}

export function runningTask(name: string, tasks: MissionTask[] | undefined): MissionTask | null {
  const mine = tasks?.filter((t) => t.agent === name) ?? [];
  return (
    mine.find((t) => t.state === 'running') ?? mine.find((t) => t.state === 'waiting_gpu') ?? null
  );
}

/** Il lavoro in corso, se no l'ultima cosa detta, se no lo stato della sessione. */
export function doing(
  a: AgentAtWork,
  tasks: MissionTask[] | undefined,
  s: Session | undefined,
): string {
  const running = runningTask(a.instanceId, tasks);
  if (running) return running.title || running.task;
  if (a.said) return plain(a.said.text);
  return s ? SESSION_META[s.state].label : 'Al lavoro';
}

export type CrewState = 'working' | 'idle' | 'done';

export interface CrewMember {
  name: string;
  state: CrewState;
  /** cosa sta facendo o cosa ha fatto, per il cartellino */
  what: string;
}

/** I membri di una sessione: il team del piano e chi ha scritto nella traccia (il PM, gli altri),
 * nell'ordine in cui compaiono, con il loro stato. */
export function crewOf(
  s: Session,
  tasks: MissionTask[] | undefined,
  trace: TraceEntry[],
  now: number,
): CrewMember[] {
  const open = !['completed', 'cancelled', 'failed'].includes(s.state);
  const names: string[] = [];
  const last = new Map<string, TraceEntry>();
  const said = new Map<string, TraceEntry>();
  for (const t of trace) {
    if (t.sessionId !== s.id || !t.instanceId) continue;
    if (!names.includes(t.instanceId)) names.push(t.instanceId);
    const seen = last.get(t.instanceId);
    if (!seen || t.seq > seen.seq) last.set(t.instanceId, t);
    if (SAID.has(t.kind) && !t.streaming && t.text.trim()) {
      const was = said.get(t.instanceId);
      if (!was || t.seq > was.seq) said.set(t.instanceId, t);
    }
  }
  for (const t of tasks ?? []) if (!names.includes(t.agent)) names.push(t.agent);

  return names.map((name) => {
    const mine = tasks?.filter((t) => t.agent === name) ?? [];
    const running = runningTask(name, tasks);
    const recent = last.get(name);
    const fresh = !!recent && now - new Date(recent.at).getTime() < ACTIVE_MS;
    const working = open && (!!running || (fresh && !mine.some((t) => t.state === 'queued')));
    const finished = mine.length > 0 && mine.every((t) => t.state === 'done');
    const words = said.get(name);
    let what: string;
    if (running) what = running.title || running.task;
    else if (working && words) what = plain(words.text);
    else if (finished) what = `Ha finito: ${mine.map((t) => t.title).join(', ')}`;
    else if (mine.some((t) => t.state === 'queued'))
      what = `In attesa: ${mine.find((t) => t.state === 'queued')?.title ?? ''}`;
    else what = words ? plain(words.text) : SESSION_META[s.state].label;
    return {
      name,
      state: working ? 'working' : finished ? 'done' : 'idle',
      what,
    };
  });
}
